import type { GateLevelSource } from "../../types/definitions/gating/gating-level-source.js";
import { GatingState } from "../../types/definitions/gating/gating-state.js";
import { GatingLevel } from "../../types/definitions/gating/gating-level.js";
import { type MarketoFormSettings } from "../marketo-form/marketo-form-setting.js";
import type { AnyFormSettingsKey } from "./form-settings-keys.js";
import { createExperienceSettings } from "./create-experience-settings.js";
import { excludeFrom } from "@mcwd/typescript-type-guards";

const AnyGate = GatingState.FormExperience;

export const FormExperienceSettingsObject = {
  contact: createExperienceSettings({
    formSettingsKey: "contact",
    mktoKey: "contact",
    formTrackingName: "Contact Form",
    canUseModal: true,
    gateLevelSource: ["Self"],
    allowedGatingStates: ["FullGate"]
  }),
  document: createExperienceSettings({
    formSettingsKey: "document",
    mktoKey: "resource",
    formTrackingName: "Document Request Form",
    canUseModal: true,
    gateLevelSource: ["Document"],
    allowedGatingStates: AnyGate
  }),
  video: createExperienceSettings({
    formSettingsKey: "video",
    mktoKey: "resource",
    formTrackingName: "Video Form",
    canUseModal: true,
    gateLevelSource: ["Document"],
    allowedGatingStates: AnyGate
  }),
  "document-landing-page": createExperienceSettings({
    formSettingsKey: "document-landing-page",
    mktoKey: "resource",
    formTrackingName: "Document Landing Page Form",
    canUseModal: false,
    gateLevelSource: ["Document"],
    allowedGatingStates: AnyGate
  }),
  "video-landing-page": createExperienceSettings({
    formSettingsKey: "video-landing-page",
    mktoKey: "resource",
    formTrackingName: "Video Landing Page Form",
    canUseModal: false,
    gateLevelSource: ["Video"],
    allowedGatingStates: AnyGate
  }),
  "video-gated": createExperienceSettings({
    formSettingsKey: "video-gated",
    mktoKey: "resource",
    formTrackingName: "Video Gated Form",
    canUseModal: true,
    gateLevelSource: ["Video"],
    allowedGatingStates: AnyGate
  }),
  "qx-demo-video": createExperienceSettings({
    formSettingsKey: "qx-demo-video",
    mktoKey: "custom-resource",
    formTrackingName: "Qx Demo Video Form",
    canUseModal: false,
    gateLevelSource: ["Self"],
    allowedGatingStates: ["FullGate"]
  }),
  "post-event-assets": createExperienceSettings({
    formSettingsKey: "post-event-assets",
    mktoKey: "resource",
    formTrackingName: "Post-Event Assets Form",
    canUseModal: false,
    gateLevelSource: ["Document", "Video", "WidgetDataSetup"],
    allowedGatingStates: excludeFrom(AnyGate, "NoGate")
  }),
  ppc: createExperienceSettings({
    formSettingsKey: "ppc",
    mktoKey: "resource",
    formTrackingName: "Single Asset Download Form",
    canUseModal: false,
    gateLevelSource: ["Document"],
    allowedGatingStates: excludeFrom(AnyGate, "NoGate"),
  }),
  "ppc-contact": createExperienceSettings({
    formSettingsKey: "ppc-contact",
    mktoKey: "contact",
    formTrackingName: "PPC Contact Form",
    canUseModal: false,
    gateLevelSource: ["Self"],
    allowedGatingStates: ["FullGate"]
  }),
  "ppc-video": createExperienceSettings({
    formSettingsKey: "ppc-video",
    mktoKey: "resource",
    formTrackingName: "PPC Video Form",
    canUseModal: false,
    gateLevelSource: ["Video"],
    allowedGatingStates: excludeFrom(AnyGate, "NoGate"),
  }),
  "qx-roi": createExperienceSettings({
    formSettingsKey: "qx-roi",
    mktoKey: "roi",
    formTrackingName: "Qx ROI Calculator Form",
    canUseModal: true,
    gateLevelSource: ["Self"],
    allowedGatingStates: ["FullGate"]
  }),
  "mx-roi": createExperienceSettings({
    formSettingsKey: "mx-roi",
    mktoKey: "roi",
    formTrackingName: "Mx ROI Calculator Form",
    canUseModal: true,
    gateLevelSource: ["Self"],
    allowedGatingStates: ["FullGate"]
  }),
  spark: createExperienceSettings({
    formSettingsKey: "spark",
    mktoKey: "spark",
    formTrackingName: "Spark Form",
    canUseModal: false,
    gateLevelSource: ["Self"],
    allowedGatingStates: ["FullGate"]
  }),
  pricing: createExperienceSettings({
    formSettingsKey: "pricing",
    mktoKey: "pricing",
    formTrackingName: "Large Pricing Form",
    canUseModal: false,
    gateLevelSource: ["Self"],
    allowedGatingStates: ["FullGate"]
  }),
  subscription: createExperienceSettings({
    formSettingsKey: "subscription",
    mktoKey: "subscription",
    formTrackingName: "Newsletter Subscription Form",
    canUseModal: false,
    gateLevelSource: ["Self"],
    allowedGatingStates: ["FullGate"]
  }),
  partner: createExperienceSettings({
    formSettingsKey: "partner",
    mktoKey: "partner",
    formTrackingName: "Partner Form",
    canUseModal: false,
    gateLevelSource: ["Self"],
    allowedGatingStates: ["FullGate"]
  }),
  "case-study": createExperienceSettings({
    formSettingsKey: "case-study",
    mktoKey: "case-study",
    formTrackingName: "Case Study Form",
    canUseModal: true,
    gateLevelSource: ["Self"],
    allowedGatingStates: ["PartialGate"]
  }),
  "demo-request": createExperienceSettings({
    formSettingsKey: "demo-request",
    mktoKey: "demo-request",
    formTrackingName: "Manufacturing Demo Form",
    canUseModal: true,
    gateLevelSource: ["Self"],
    allowedGatingStates: ["FullGate"]
  }),
  "tradeshow-giveaway": createExperienceSettings({
    formSettingsKey: "tradeshow-giveaway",
    mktoKey: "tradeshow-giveaway",
    formTrackingName: "Tradeshow Giveaway Form",
    canUseModal: false,
    gateLevelSource: ["Self"],
    allowedGatingStates: ["FullGate"]
  }),
  "event-registration": createExperienceSettings({
    formSettingsKey: "event-registration",
    mktoKey: "event-registration",
    formTrackingName: "Event Registration Form",
    canUseModal: true,
    gateLevelSource: ["Self"],
    allowedGatingStates: ["FullGate"]
  }),
  "subprocessor-update-notification": createExperienceSettings({
    formSettingsKey: "subprocessor-update-notification",
    mktoKey: "subprocessor-update-notification",
    formTrackingName: "Sub-Processor Update Notification Form",
    canUseModal: false,
    gateLevelSource: ["Self"],
    allowedGatingStates: ["FullGate"]
  }),
  "digital-maturity-assessment": createExperienceSettings({
    formSettingsKey: "digital-maturity-assessment",
    mktoKey: "maturity-assessment",
    formTrackingName: "Digital Maturity Form",
    canUseModal: true,
    gateLevelSource: ["Self"],
    allowedGatingStates: ["FullGate"]
  }),
} as const;
export type FormExperienceSettingsObject = typeof FormExperienceSettingsObject;

const _formExperienceSettingsArray = Object.freeze(Object.values(FormExperienceSettingsObject));

export type FormTrackingName = (typeof _formExperienceSettingsArray)[number]["formTrackingName"];
const FormTrackingName: FormTrackingName[] = _formExperienceSettingsArray.map(s => s.formTrackingName);


export interface FormExperienceSettings<TAllowedGatingState extends GatingState.FormExperience = GatingState.FormExperience> extends MarketoFormSettings<GatingLevel.MarketoForm> {
  readonly formSettingsKey: AnyFormSettingsKey,
  readonly formTrackingName: FormTrackingName,
  readonly canUseModal: boolean,
  readonly gateLevelSource: GateLevelSource[]
  readonly allowedGatingStates: readonly TAllowedGatingState[];
  readonly defaultGateLevel?: (TAllowedGatingState & ("FullGate" | "PartialGate"));
}

export const FormExperienceSettings = _formExperienceSettingsArray as unknown as readonly FormExperienceSettings[];
